import React, { useState, useEffect } from 'react';
import { riskAnalysisService } from '../../services/riskAnalysisService';
import RiskAnalysisCard from './RiskAnalysisCard';
import RiskAnalysisDetails from './RiskAnalysisDetails';
import RiskAnalysisButton from './RiskAnalysisButton';
import LoadingSpinner from '../UI/LoadingSpinner';
import { Button } from '../UI/button';
import { ModalDialog } from '../UI/modal-dialog';
import { useLocale } from '../../contexts/LocaleContext';
import { themeClasses } from '../../config/themeClasses';
import { cn } from '../../lib/utils';

const RiskAnalysisModal = ({
  isOpen,
  onClose,
  ticker,
  companyName,
  initialAnalysis = null,
}) => {
  const { t } = useLocale();
  const [analysis, setAnalysis] = useState(initialAnalysis);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cacheStatus, setCacheStatus] = useState(null);

  useEffect(() => {
    if (isOpen && ticker) {
      checkCachedAnalysis();
    }
  }, [isOpen, ticker]);

  const checkCachedAnalysis = async () => {
    if (!ticker) return;
    try {
      const cachedResult = await riskAnalysisService.getCachedRiskAnalysis(ticker);
      if (cachedResult && !cachedResult.cacheStatus.isExpired) {
        setAnalysis(cachedResult.riskAnalysis);
        setCacheStatus(cachedResult.cacheStatus);
      }
    } catch (err) {
    }
  };

  const handleAnalysisComplete = (newAnalysis, fromCache, cacheAge) => {
    setAnalysis(newAnalysis);
    setError(null);
    setCacheStatus({ hasCache: true, isExpired: false, ageMinutes: cacheAge || 0 });
  };

  const handleAnalysisError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleClose = () => {
    setAnalysis(null);
    setError(null);
    setCacheStatus(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalDialog
      open={isOpen}
      title={t('analyzeRisk')}
      description={`${companyName} (${ticker})`}
      onClose={handleClose}
      closeLabel={t('dialogCloseLabel')}
      className="max-w-4xl"
      footer={
        <Button type="button" variant="outline" onClick={handleClose}>
          {t('close')}
        </Button>
      }
    >
      {cacheStatus ? (
        <p className="text-xs text-text-secondary mb-4">
          {cacheStatus.hasCache && !cacheStatus.isExpired
            ? t('riskAnalysisCacheFresh', { minutes: cacheStatus.ageMinutes })
            : t('riskAnalysisCacheExpired', { minutes: cacheStatus.ageMinutes })}
        </p>
      ) : null}

      <div className="max-h-[60vh] overflow-y-auto space-y-6">
        {loading && !analysis ? (
          <div className="flex flex-col items-center justify-center py-12">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-text-secondary">{t('riskAnalysisPerforming')}</p>
            <p className="mt-2 text-sm text-text-muted">{t('riskAnalysisPerformingHint')}</p>
          </div>
        ) : null}

        {error ? (
          <div className={cn(themeClasses.appKit.emptyDashed, 'border-danger-500/30 bg-danger-500/5 py-6')}>
            <p className="text-sm font-medium text-danger-500">{t('riskAnalysisErrorTitle')}</p>
            <p className="mt-1 text-sm text-text-secondary">{error}</p>
          </div>
        ) : null}

        {!loading && !analysis && !error ? (
          <div className={themeClasses.appKit.emptyDashed}>
            <p className={themeClasses.appKit.sectionTitle}>{t('noRiskAnalysis')}</p>
            <p className="mt-2 text-sm text-text-secondary mb-6">{t('noRiskAnalysisCta')}</p>
            <RiskAnalysisButton
              ticker={ticker}
              companyName={companyName}
              onAnalysisComplete={handleAnalysisComplete}
              onError={handleAnalysisError}
              variant="primary"
            />
          </div>
        ) : null}

        {analysis ? (
          <div className="space-y-6">
            <RiskAnalysisCard riskAnalysis={analysis} />
            <div className="flex flex-wrap items-center gap-3 border-t border-border-light pt-4">
              <RiskAnalysisButton
                ticker={ticker}
                companyName={companyName}
                onAnalysisComplete={handleAnalysisComplete}
                onError={handleAnalysisError}
                variant="outline"
              />
              {cacheStatus?.hasCache && !cacheStatus?.isExpired ? (
                <span className="text-sm text-success-500">{t('riskAnalysisUsingCache')}</span>
              ) : null}
            </div>
            <RiskAnalysisDetails riskAnalysis={analysis} />
          </div>
        ) : null}
      </div>
    </ModalDialog>
  );
};

export default RiskAnalysisModal;
